import { useEffect, useState } from "react";
import { Loading } from "@/components/Loading";
import { Link, useNavigate, useParams } from "react-router-dom";
import { backOrigin, useBackTo } from "@/hooks/useBackTo";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { PermissionEditor } from "@/features/users/components/PermissionEditor";
import { ApiError } from "@/lib/apiClient";
import { toastApiError } from "@/lib/toast";
import { permissions as permissionsApi, roles as rolesApi, type PermissionCatalogEntry, type RoleGrant } from "@/features/users/api/users.api";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { TextField, useAutoFocus, useUnsavedChangesWarning } from "@/components/form";
import { Notice } from "@/components/Notice";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useConfirm } from "@/components/ConfirmDialog";
import { FIELD_MAX, tooLong } from "@/lib/fieldLimits";

const schema = z.object({
  name: z.string().trim().min(1, "Role name is required.").max(FIELD_MAX.role.name, tooLong("Name", FIELD_MAX.role.name)),
  description: z.string().max(FIELD_MAX.role.description, tooLong("Description", FIELD_MAX.role.description)),
});
type FormValues = z.infer<typeof schema>;

export default function RoleEditPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const backTo = useBackTo("/manage/roles");
  const isEdit = id !== undefined;

  const [isSystem, setIsSystem] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState(0);

  const [grants, setGrants] = useState<Map<string, RoleGrant>>(new Map());
  const [originalGrants, setOriginalGrants] = useState<Map<string, RoleGrant> | null>(null);

  const [catalog, setCatalog] = useState<PermissionCatalogEntry[] | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [savingGrants, setSavingGrants] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const confirm = useConfirm();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", description: "" },
  });

  useAutoFocus(form, "name", !isEdit);

  useEffect(() => {
    let cancelled = false;

    permissionsApi.catalog()
      .then((c) => { if (!cancelled) setCatalog(c); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load permission catalog."); });

    if (isEdit && id) {
      rolesApi.get(id)
        .then((r) => {
          if (cancelled) return;
          form.reset({ name: r.name, description: r.description ?? "" });
          setIsSystem(r.isSystem);
          const m = new Map<string, RoleGrant>();
          for (const g of r.grants) m.set(g.permissionId, g);
          setGrants(m);
          setOriginalGrants(new Map(m));
          setAssignedUsers(r.userCount);
          setLoading(false);
        })
        .catch((e) => {
          if (cancelled) return;
          if (e instanceof ApiError && e.status === 404) setNotFound(true);
          else setError(e instanceof Error ? e.message : "Failed to load role.");
          setLoading(false);
        });
    }

    return () => { cancelled = true; };
  }, [id, isEdit, form]);

  async function onSaveMeta(v: FormValues) {
    if (isEdit && !form.formState.isDirty) {
      toast.info("No changes to save");
      return;
    }
    setError(null);
    try {
      if (isEdit && id) {
        await rolesApi.update(id, { name: v.name.trim(), description: v.description || null });
      } else {
        const created = await rolesApi.create({
          name: v.name.trim(),
          description: v.description || null,
          grants: [],
        });
        toast.success("Role created");
        navigate(`/manage/roles/${created.id}`, { replace: true, state: backOrigin(backTo) });
      }
    } catch (e) {
      toastApiError(e, "Save failed.");
    }
  }

  async function onSaveGrants() {
    if (!id) return;
    setError(null);
    setSavingGrants(true);
    try {
      await rolesApi.setGrants(id, Array.from(grants.values()));
      setOriginalGrants(new Map(grants));
    } catch (e) {
      toastApiError(e, "Failed to save grants.");
    } finally {
      setSavingGrants(false);
    }
  }

  async function onDelete() {
    if (!id) return;
    if (!(await confirm({ description: "Delete this role? This can't be undone.", confirmLabel: "Delete", destructive: true }))) return;
    setError(null);
    try {
      await rolesApi.remove(id);
      navigate(backTo, { replace: true });
    } catch (e) {
      toastApiError(e, "Delete failed.");
    }
  }

  const deleteBlockedReason = isSystem
    ? "Default roles can't be deleted."
    : assignedUsers > 0
      ? `This role is assigned to ${assignedUsers} ${assignedUsers === 1 ? "user" : "users"} and can't be deleted.`
      : null;

  const grantsDirty = isEdit && originalGrants !== null && !grantMapsEqual(grants, originalGrants);
  useUnsavedChangesWarning(form.formState.isDirty || grantsDirty);
  const savingMeta = form.formState.isSubmitting;

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-4">
        <Link to={backTo} className="text-sm text-muted-foreground hover:underline">
          ← Back to roles
        </Link>
      </div>

      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">
            {isEdit ? "Edit role" : "New role"}
          </h1>
        </div>
        {isEdit && !loading && !notFound && (
          deleteBlockedReason !== null ? (
            <BlockedDeleteButton reason={deleteBlockedReason} />
          ) : (
            <Button type="button" variant="destructive" onClick={onDelete}>
              Delete role
            </Button>
          )
        )}
      </header>

      {notFound ? (
        <Notice tone="info">Role not found.</Notice>
      ) : loading ? (
        <Loading>Loading role…</Loading>
      ) : (
        <>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSaveMeta)}
              className="bg-white border border-slate-200 rounded-md shadow-sm p-6 mb-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField control={form.control} name="name" label="Name" required autoComplete="off" />
                <TextField control={form.control} name="description" label="Description" autoComplete="off" />
              </div>

              {error && <Notice tone="error" className="mt-4">{error}</Notice>}

              <div className="mt-4 flex items-center justify-end gap-3">
                <Button asChild variant="ghost">
                  <Link to={backTo}>Cancel</Link>
                </Button>
                <Button type="submit" disabled={savingMeta}>
                  {savingMeta ? "Saving…" : isEdit ? "Save name & description" : "Create role"}
                </Button>
              </div>
            </form>
          </Form>

          {isEdit && (
            <section>
              <header className="mb-3 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">Permissions</h2>
                  <p className="text-sm text-muted-foreground">
                    Toggle Use to grant. Reassign lets the user delegate
                    the permission to others (rare; leave off if unsure).
                  </p>
                </div>
                <Button type="button" variant="outline" onClick={onSaveGrants} disabled={savingGrants || !grantsDirty}>
                  {savingGrants ? "Saving grants…" : grantsDirty ? "Save grants" : "Grants saved"}
                </Button>
              </header>

              {catalog === null ? (
                <Loading>Loading permission catalog…</Loading>
              ) : (
                <PermissionEditor
                  catalog={catalog}
                  grants={grants}
                  onChange={setGrants}
                />
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}

function BlockedDeleteButton({ reason }: { reason: string }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="cursor-not-allowed">
            <Button type="button" variant="destructive" disabled className="pointer-events-none">
              Delete role
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{reason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function grantMapsEqual(a: Map<string, RoleGrant>, b: Map<string, RoleGrant>): boolean {
  if (a.size !== b.size) return false;
  for (const [key, va] of a) {
    const vb = b.get(key);
    if (!vb || vb.canUse !== va.canUse || vb.canReassign !== va.canReassign) return false;
  }
  return true;
}
